import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useCart } from '../contexts/CartContext'
import { productAPI, configAPI } from '../services/api'
import AddToCartNotification from '../components/AddToCartNotification'
import './ProductDetail.css'
import { useDocumentMeta } from '../seo/useDocumentMeta'

function ProductDetail() {
  const { addToCart } = useCart();
  const { id } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [imageError, setImageError] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [selectedSize, setSelectedSize] = useState(null)
  const [selectedSku, setSelectedSku] = useState(null)
  const [currentPrice, setCurrentPrice] = useState(null)
  const [currentStock, setCurrentStock] = useState(null)
  const [expandedSections, setExpandedSections] = useState({
    shipping: false,
    details: false,
    size: false
  })
  const [showAddToCartNotification, setShowAddToCartNotification] = useState(false)
  const [addedProduct, setAddedProduct] = useState(null)
  const [addedQuantity, setAddedQuantity] = useState(1)
  const [config, setConfig] = useState({
    freeShippingThreshold: 30
  })

  const handleImageError = () => {
    setImageError(true)
  }

  // 商品还没加载出来时走默认文案；不要在这里拼半截标题，爬虫可能就抓到那一帧。
  // notFound 要等加载结束才算数，否则加载中的那一帧会被标成 noindex。
  useDocumentMeta({ product, notFound: !loading && !product })

  // 当产品加载时，重置选中的图片索引
  useEffect(() => {
    if (product && product.images && product.images.length > 0) {
      setSelectedImageIndex(0)
      setImageError(false)
    }
  }, [product])

  // 当产品加载时，重置选中的图片索引
  useEffect(() => {
    if (product && product.images && product.images.length > 0) {
      setSelectedImageIndex(0)
      setImageError(false)
    }
  }, [product])

  // 获取配置信息
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const result = await configAPI.getConfig()
        if (result.success && result.data) {
          setConfig({
            freeShippingThreshold: result.data.freeShippingThreshold
          })
        }
      } catch (error) {
        console.error('Failed to fetch config:', error)
        // 使用默认值
      }
    }
    fetchConfig()
  }, [])

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true)
        const result = await productAPI.getProduct(id)
        
        if (result.success) {
          setProduct(result.data)
          // 如果有 variants，设置默认选择并立即更新价格和库存
          if (result.data.variants && Object.keys(result.data.variants).length > 0) {
            const firstSize = result.data.sizes?.[0] || null
            const firstSku = result.data.skus?.[0] || null
            if (firstSize && firstSku) {
              setSelectedSize(firstSize)
              setSelectedSku(firstSku)
              // 立即从 variants 中获取对应的价格和库存
              const sizeVariants = result.data.variants[firstSize]
              if (sizeVariants && sizeVariants[firstSku]) {
                const variant = sizeVariants[firstSku]
                setCurrentPrice(variant.price)
                setCurrentStock(variant.stock)
              }
            }
          } else {
            // 没有 variants，使用基础价格
            setCurrentPrice(result.data.price)
            setCurrentStock(result.data.stock)
          }
        } else {
          throw new Error(result.message || '获取产品详情失败')
        }
      } catch (err) {
        console.error('获取产品详情失败:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchProduct()
    }
  }, [id])

  // 换了规格之后，把数量收敛到新规格的库存上限。
  // 不收敛的话：在库存 10 的规格上选了 5 件，切到库存 1 的规格，数量还是 5，
  // 而后端目前不校验库存，这一单会直接超卖。
  useEffect(() => {
    if (currentStock !== null && currentStock >= 0 && quantity > currentStock) {
      setQuantity(currentStock > 0 ? currentStock : 1)
    }
  }, [currentStock, quantity])

  // 当 Size 或 SKU 改变时，更新价格和库存
  useEffect(() => {
    if (product && product.variants && Object.keys(product.variants).length > 0) {
      // 如果有 variants，需要同时选择 Size 和 SKU
      if (selectedSize && selectedSku) {
        const sizeVariants = product.variants[selectedSize]
        if (sizeVariants && sizeVariants[selectedSku]) {
          const variant = sizeVariants[selectedSku]
          // 确保 variant 有 price 和 stock 属性
          if (variant && typeof variant.price !== 'undefined' && typeof variant.stock !== 'undefined') {
            setCurrentPrice(variant.price)
            setCurrentStock(variant.stock)
          } else {
            console.warn('Variant missing price or stock:', variant)
            setCurrentPrice(null)
            setCurrentStock(null)
          }
        } else {
          // 如果选择的组合不存在，重置为 null 或显示提示
          setCurrentPrice(null)
          setCurrentStock(null)
        }
      } else {
        // 如果还没有选择完整的 Size 和 SKU，不显示价格
        setCurrentPrice(null)
        setCurrentStock(null)
      }
    } else if (product && (!product.variants || Object.keys(product.variants).length === 0)) {
      // 没有 variants，使用基础价格
      setCurrentPrice(product.price)
      setCurrentStock(product.stock)
    }
  }, [product, selectedSize, selectedSku])

  // 加载状态
  if (loading) {
    return (
      <div className="product-detail">
        <div className="loading-container">
          <h2>Loading...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    )
  }

  // 错误状态
  if (error) {
    return (
      <div className="product-detail">
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    )
  }

  // 产品不存在
  if (!product) {
    return (
      <div className="product-detail">
        <div className="error-container">
          <h2>Error</h2>
          <p>Failed to load product details</p>
        </div>
      </div>
    )
  }

  const handleQuantityChange = (newQuantity) => {
    const maxStock = currentStock !== null ? currentStock : product.stock
    if (newQuantity >= 1 && newQuantity <= maxStock) {
      setQuantity(newQuantity)
    }
  }

  const handleAddToCart = () => {
    // 如果有 variants，需要传递选中的 Size 和 SKU
    const cartItem = {
      ...product,
      selectedSize,
      selectedSku,
      price: currentPrice !== null ? currentPrice : product.price,
      selectedPrice: currentPrice !== null ? currentPrice : product.price
    }
    addToCart(cartItem, quantity);
    
    // 显示通知
    setAddedProduct(cartItem)
    setAddedQuantity(quantity)
    setShowAddToCartNotification(true)
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  return (
    <div className="product-detail">
      <div className="product-container">
        <div className="product-images">
          {/* 图片缩略图 - 左侧 */}
          {product.images && product.images.length > 0 && (
          <div className="image-thumbnails">
              {product.images.map((image, index) => (
            <img 
                  key={index}
                  src={image} 
                  alt={`${product.name} - Image ${index + 1}`}
                  className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                />
              ))}
          </div>
          )}
          
          {/* 主图 - 右侧 */}
          <div className="main-image-container">
            {product.images && product.images.length > 0 ? (
              !imageError ? (
              <img 
                  src={product.images[selectedImageIndex]} 
                alt={product.name} 
                className="main-image"
                onError={handleImageError}
              />
            ) : (
              <div className="image-placeholder">
                <div className="placeholder-content">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="#ccc">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  <p>Image not available</p>
                  </div>
                </div>
              )
            ) : (
              <div className="image-placeholder">
                <div className="placeholder-content">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="#ccc">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                  <p>No images available</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="product-info">
          <h1>{product.name?.en || product.name}</h1>
          <p className="category">{product.category}</p>
          <p className="price">
            {currentPrice !== null 
              ? `$${currentPrice}` 
              : product.variants && Object.keys(product.variants).length > 0 && (!selectedSize || !selectedSku)
              ? 'Please select Size and Style'
              : `$${product.price || 0}`
            }
          </p>
          {/* Size 选择器 */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="size-selector">
              <label>Size:</label>
              <div className="option-buttons">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    className={`option-button ${selectedSize === size ? 'selected' : ''}`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* SKU 选择器 */}
          {product.skus && product.skus.length > 0 && (
            <div className="sku-selector">
              <label>Style:</label>
              <div className="option-buttons">
                {product.skus.map((sku) => (
                  <button
                    key={sku}
                    className={`option-button ${selectedSku === sku ? 'selected' : ''}`}
                    onClick={() => setSelectedSku(sku)}
                  >
                    {sku}
                  </button>
                ))}
              </div>
          </div>
          )}
          
          <div className="quantity-and-actions">
          <div className="quantity-selector">
              <label>Qty:</label>
            <button 
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
            >
              -
            </button>
            <span>{quantity}</span>
            <button 
              onClick={() => handleQuantityChange(quantity + 1)}
                disabled={quantity >= (currentStock !== null ? currentStock : product.stock)}
            >
              +
            </button>
          </div>
          
          <div className="product-actions">
              <button 
                className="add-to-cart-btn" 
                onClick={handleAddToCart}
                disabled={
                  product.variants && Object.keys(product.variants).length > 0 
                    ? (!selectedSize || !selectedSku || currentPrice === null || (currentStock !== null && currentStock === 0))
                    : (product.stock === 0)
                }
              >
                {product.variants && Object.keys(product.variants).length > 0 
                  ? (!selectedSize || !selectedSku
                      ? 'PLEASE SELECT SIZE AND STYLE'
                      : currentPrice === null
                      ? 'UNAVAILABLE'
                      : currentStock !== null && currentStock === 0
                      ? 'OUT OF STOCK'
                      : 'ADD TO BAG')
                  : (product.stock === 0
                      ? 'OUT OF STOCK'
                      : 'ADD TO BAG')
                }
            </button>
            </div>
          </div>

          {/* 可折叠的详情区域 */}
          <div className="collapsible-sections">
            <div className="collapsible-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('shipping')}
              >
                <span>FREE SHIPPING & RETURNS</span>
                <span className="toggle-icon">{expandedSections.shipping ? '−' : '+'}</span>
              </button>
              {expandedSections.shipping && (
                <div className="section-content">
                  <p>Free shipping on all orders over ${config.freeShippingThreshold}. Easy returns within 14 days.</p>
                </div>
              )}
            </div>

            <div className="collapsible-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('details')}
              >
                <span>PRODUCT DETAILS</span>
                <span className="toggle-icon">{expandedSections.details ? '−' : '+'}</span>
              </button>
              {expandedSections.details && (
                <div className="section-content">
                  {product.productDetails ? (
                    <div className="details-content">
                      {product.productDetails.split('\n').map((line, index) => (
                        line.trim() && (
                          <div key={index} className="detail-line">
                            {line.trim()}
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <p>No product details available.</p>
                  )}
                </div>
              )}
            </div>

            <div className="collapsible-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('size')}
              >
                <span>SIZE AND FIT</span>
                <span className="toggle-icon">{expandedSections.size ? '−' : '+'}</span>
              </button>
              {expandedSections.size && (
                <div className="section-content">
                  {product.sizeAndFit ? (
                    <div className="size-fit-content">
                      {product.sizeAndFit.split('\n').map((line, index) => (
                        line.trim() && (
                          <div key={index} className="size-fit-line">
                            {line.trim()}
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                  <p>Please refer to the product specifications for detailed size information. Contact us if you need assistance with sizing.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {showAddToCartNotification && addedProduct && (
        <AddToCartNotification
          product={addedProduct}
          quantity={addedQuantity}
          onClose={() => setShowAddToCartNotification(false)}
        />
      )}
    </div>
  )
}

export default ProductDetail
